import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bell,
  Building2,
  CreditCard,
  FileClock,
  FileText,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings2,
  ShieldCheck,
  UserPlus,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import ThemeToggle from "../ThemeToggle";
import { ReactComponent as BrandLogo } from "../../assets/Genielytics.svg";
import { resetAppStorageForLogout } from "../../utils/scopedStorage";
import "./SuperAdminConsole.css";

const navItems = [
  {
    to: "/platform",
    label: "Platform dashboard",
    icon: BarChart3,
    end: true,
  },
  {
    to: "/platform/customers",
    label: "Customers",
    icon: Building2,
    badge: "12",
  },
  {
    to: "/platform/onboarding",
    label: "Onboard customer",
    icon: UserPlus,
  },
  {
    to: "/platform/billing",
    label: "Billing & quotas",
    icon: CreditCard,
  },
  {
    to: "/platform/audit",
    label: "Audit log",
    icon: FileClock,
  },
  {
    to: "/platform/settings",
    label: "Platform settings",
    icon: Settings2,
  },
];

const onPremNavItems = [
  { to: "/platform", label: "Overview", icon: BarChart3, end: true },
  { to: "/platform/onboarding", label: "Onboarding", icon: UserPlus },
  { to: "/platform/logs", label: "Logs", icon: FileText },
];

const getUserInitials = (name = "Super Admin") =>
  String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

export default function SuperAdminLayout({ sessionProfile, workspace = "managed" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const idleTimerRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 960px)").matches
      : false
  );

  const user = sessionProfile?.user || {};
  const isOnPrem = workspace === "onprem";
  const activeNavItems = isOnPrem ? onPremNavItems : navItems;

  const handleLogout = useCallback(
    (reason = "manual") => {
      resetAppStorageForLogout();

      if (reason === "idle") {
        toast.info("You were logged out due to inactivity");
      } else {
        toast.success("Logged out successfully!");
      }

      navigate("/login", { replace: true });
    },
    [navigate]
  );

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      handleLogout("idle");
    }, 15 * 60 * 1000);
  }, [handleLogout]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 960px)");
    const handler = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer));
    resetIdleTimer();

    return () => {
      events.forEach((eventName) =>
        window.removeEventListener(eventName, resetIdleTimer)
      );
      clearTimeout(idleTimerRef.current);
    };
  }, [resetIdleTimer]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="platform-shell">
      {isMobile && sidebarOpen ? (
        <button
          type="button"
          className="platform-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close navigation"
        />
      ) : null}

      <aside
        className={`platform-sidebar ${isMobile ? "is-mobile" : ""} ${
          sidebarCollapsed ? "is-collapsed" : ""
        } ${
          sidebarOpen ? "is-open" : ""
        }`}
      >
        <div className="platform-sidebar-brand">
          <div className="platform-brand-mark" aria-hidden="true">
            <BrandLogo className="platform-brand-logo" />
          </div>
          <div className="platform-brand-copy">
            <div className="platform-brand-title">Geneilytics</div>
            <div className="platform-brand-subtitle">
              {isOnPrem ? "On-prem administration" : "Superadmin console"}
            </div>
          </div>
          {!isMobile ? (
            <button
              type="button"
              className="platform-icon-button platform-collapse-toggle"
              onClick={() => setSidebarCollapsed((current) => !current)}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
          ) : null}
          {isMobile ? (
            <button
              type="button"
              className="platform-icon-button only-mobile"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>

        <div className="platform-sidebar-scroll">
          <div className="platform-sidebar-section-label">Overview</div>
          <nav className="platform-nav">
            {activeNavItems.map(({ to, label, icon: Icon, badge, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                title={sidebarCollapsed && !isMobile ? label : undefined}
                className={({ isActive }) =>
                  `platform-nav-item ${isActive ? "is-active" : ""}`
                }
              >
                <Icon size={16} />
                <span className="platform-nav-label">{label}</span>
                {badge ? <span className="platform-nav-badge">{badge}</span> : null}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="platform-sidebar-footer">
          <div className="platform-user-card">
            <div className="platform-user-avatar">{getUserInitials(user.name)}</div>
            <div className="platform-user-copy">
              <strong>{user.name || "Super Admin"}</strong>
              <span>{isOnPrem ? "Platform superadmin" : user.platform_role || "Platform owner"}</span>
            </div>
          </div>

          <button
            type="button"
            className="platform-logout-button"
            onClick={() => handleLogout("manual")}
          >
            <LogOut size={15} />
            Logout
          </button>
        </div>
      </aside>

      <div className="platform-main">
        <header className="platform-topbar">
          <div className="platform-topbar-left">
            {isMobile ? (
              <button
                type="button"
                className="platform-icon-button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open navigation"
              >
                <Menu size={16} />
              </button>
            ) : null}

            <div>
              <div className="platform-topbar-title">Geneilytics</div>
              <div className="platform-topbar-subtitle">
                {isOnPrem ? "Installation administration" : "Powered by Generative AI"}
              </div>
            </div>
          </div>

          <div className="platform-topbar-actions">
            <ThemeToggle className="platform-theme-toggle" />

            {!isOnPrem ? (
              <>
                <button type="button" className="platform-icon-button" aria-label="Security status">
                  <ShieldCheck size={16} />
                </button>
                <button type="button" className="platform-icon-button" aria-label="Notifications">
                  <Bell size={16} />
                </button>
              </>
            ) : null}
          </div>
        </header>

        <main className="platform-content">
          <Outlet context={{ sessionProfile }} />
        </main>
      </div>
    </div>
  );
}
